import React from 'react'
import styled from 'styled-components'
import { InputField } from 'src/components/form'
import ButtonCustom from 'src/components/form/Button'
import {
  ChangeRequestFormValues,
  useDuplicateAccountOptions,
} from './useDuplicateAccountOptions'

const DuplicateAccountOptionsContainer: React.FC = () => {
  const {
    type,
    label,
    hasRequiredState,
    hasAccessToken,
    userHasPhone,
    step,
    isRequestSent,
    form,
    showNameField,
    showContactEmailField,
    goToMain,
    goToChangeValue,
    goToLoginOptions,
    handleGoBack,
    hasDuplicateEmailPassword,
    isCheckingNewValue,
    debouncedCheckNewValue,
    handleSubmitChangeRequest,
    handleGoToPasswordSetting,
    handleGoToMyPage,
    loginUrl,
    handleGoToContact,
  } = useDuplicateAccountOptions()

  if (!hasRequiredState) {
    return null
  }

  return (
    <Wrapper>
      <div className="container">
        <h1>会員登録について</h1>

        {step === 'main' && (
          <div className="options">
            <p className="lead">
              入力いただいた{label}は、既にご登録済みです。
              <br />
              以下のいずれかをお選びください。
            </p>

            {/* [SCOPE] No.154 — final client confirmation: for phone
                duplicates the user is already treated as logged in at this
                step, so the 問診/パスワード設定 options and a Login bounce are
                no longer offered here; only 3 actions remain. The email
                duplicate flow (type === 'email', from UserInfoForm) is
                unaffected and keeps its original 4-option behavior below. */}
            {type === 'phone' ? (
              <>
                {/* [SCOPE] Feedback 4: when user has no phone yet (entering
                    phone for the first time), show 3 fixed options without
                    the change-phone flow. When user already has a phone they
                    are updating, keep the full set including change. */}
                {!userHasPhone ? (
                  <>
                    {/* [SCOPE] P1 fix: guard マイページへ移動する with hasAccessToken —
                        a guest reaching the duplicate-phone screen during registration
                        has no session yet and would be bounced by AuthenticatedLayout. */}
                    {hasAccessToken && (
                      <button className="btn gold" onClick={handleGoToMyPage}>
                        マイページへ移動する
                      </button>
                    )}
                    <button className="btn gray" onClick={handleGoToContact}>
                      問い合わせする
                    </button>
                    <button className="btn text" onClick={handleGoBack}>
                      戻る
                    </button>
                  </>
                ) : (
                  <>
                    <button className="btn gold" onClick={goToChangeValue}>
                      {label}を変更したい
                    </button>
                    {hasAccessToken && (
                      <button className="btn gold" onClick={handleGoToMyPage}>
                        マイページへ移動する
                      </button>
                    )}
                    <button className="btn gray" onClick={handleGoToContact}>
                      問い合わせする
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button className="btn gold" onClick={goToChangeValue}>
                  {label}を変更したい
                </button>
                <button className="btn gold" onClick={goToLoginOptions}>
                  問診に回答して薬を処方してもらいたい
                </button>
                {/* Bug 1: user already has a session token from the registration
                    email step — let them go directly to My page. */}
                {hasAccessToken && (
                  <button className="btn gold" onClick={handleGoToMyPage}>
                    Myページへ
                  </button>
                )}
                <button className="btn gray" onClick={handleGoToContact}>
                  問い合わせする
                </button>
                <button className="btn text" onClick={handleGoBack}>
                  戻る
                </button>
              </>
            )}
          </div>
        )}

        {step === 'change-value' && (
          <div className="options">
            {!isRequestSent ? (
              <>
                <p className="lead">
                  新しい{label}を入力してください。
                  <br />
                  変更完了後、サポート担当よりご連絡いたします。
                </p>
                {showNameField && (
                  <InputField<ChangeRequestFormValues>
                    label="お名前"
                    controller={{
                      control: form.control,
                      name: 'name',
                    }}
                    inputProps={{
                      placeholder: '入力してください',
                    }}
                  />
                )}
                <InputField<ChangeRequestFormValues>
                  label={`新しい${label}`}
                  controller={{
                    control: form.control,
                    name: 'newValue',
                  }}
                  inputProps={{
                    placeholder: '入力してください',
                    // [SCOPE] No.154 (FB2) — flush the pending debounce on blur
                    // instead of calling handleNewValueBlur directly, avoiding
                    // a double API call when the user types then blurs.
                    onBlur: () => debouncedCheckNewValue.flush(),
                  }}
                />
                {showContactEmailField && (
                  <InputField<ChangeRequestFormValues>
                    label="ご連絡先メールアドレス"
                    controller={{
                      control: form.control,
                      name: 'contactEmail',
                    }}
                    inputProps={{
                      placeholder: '入力してください',
                    }}
                  />
                )}
                <ButtonCustom
                  className="btn-default btn-chestnut-rose br-60 mt-20"
                  loading={form.formState.isSubmitting}
                  disabled={!form.formState.isValid || isCheckingNewValue}
                  htmlType="button"
                  onClick={handleSubmitChangeRequest}
                  name="送信する"
                />
                <button className="btn text" onClick={goToMain}>
                  戻る
                </button>
              </>
            ) : (
              <p className="lead">
                ご依頼を受け付けました。1~3営業日内にいただいた{label}
                宛にサポート担当よりご連絡いたします。
              </p>
            )}
          </div>
        )}

        {step === 'login-options' && (
          <div className="options">
            <p className="lead">現在のログイン状況に合わせてお選びください。</p>

            {/* Bug 4: use button+navigate instead of <a target="_blank"> so the
                SPA router handles the transition without a full page reload.
                Opening /pwd/setting in a new tab caused the direct-access guard
                to redirect to /status-error. */}
            {/* [SCOPE] No.154 (FB4) — disable until hasDuplicateEmailPassword
                resolves (not null) to prevent routing to the wrong screen when
                the user clicks before checkDuplicateEmailHasPassword returns. */}
            <button
              className="btn gold"
              onClick={handleGoToPasswordSetting}
              disabled={hasDuplicateEmailPassword === null}
            >
              パスワードを設定する（未設定・忘れた方）
            </button>
            <a className="btn gold" href={loginUrl}>
              ログインする（パスワードをお持ちの方）
            </a>
            <button className="btn text" onClick={goToMain}>
              戻る
            </button>
          </div>
        )}
      </div>
    </Wrapper>
  )
}

export default DuplicateAccountOptionsContainer

const Wrapper = styled.div`
  background-color: #f8f5eb;
  min-height: 100vh;
  padding: 60px 20px;
  display: flex;
  justify-content: center;

  .container {
    max-width: 500px;
    width: 100%;

    h1 {
      text-align: center;
      font-weight: 900;
      font-size: 24px;
      margin-bottom: 30px;
    }

    .options {
      display: flex;
      flex-direction: column;
      gap: 16px;

      .lead {
        font-size: 14px;
        line-height: 1.6;
        margin-bottom: 10px;
      }
    }

    /* Bug 2: aga-input globally has border:none which makes inputs invisible
       on this cream background. Add a visible border in normal state and a red
       border in error state, scoped to this page only. */
    .aga-input {
      border: 1px solid #b8b8b8 !important;
    }

    .ant-form-item-has-error .aga-input,
    .aga-input.ant-input-status-error {
      border-color: #ff4d4f !important;
    }

    .btn {
      display: block;
      text-align: center;
      padding: 12px 20px;
      border-radius: 30px;
      border: none;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      text-decoration: none;

      &.gold {
        background-color: #c5b478;
        color: white;
      }

      &.gray {
        background-color: #666;
        color: white;
      }

      &.text {
        background: none;
        color: #666;
        text-decoration: underline;
      }
    }
  }
`
